import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { hasZhuyin } from "../lib/ime_composition"

// AddressAutocompleteController — self-hosted gazetteer address search (no Google Places).
// Debounced fetch → /api/v1/places/search, renders a dropdown, dispatches
// address-autocomplete:place-selected (lat/lng = place centroid; the user fine-tunes the exact spot
// via the MapLibre marker drag in map-picker).
//
// Targets:
// - input: Address input field
//
// Events:
// - address-autocomplete:place-selected → { latitude, longitude, address, placeResult }
const log = createLogger('AddressAutocomplete')

// Search debounce = 3× the base retry delay (SSOT: BusinessConstants::UITiming::RETRY_DELAY_MS).
const debounceMs = () => (window.BusinessConstants?.UITiming?.RETRY_DELAY_MS || 100) * 3

export default class extends Controller {
  static targets = ["input"]

  connect() {
    log.debug('connect')
    this.initSelfHostedAutocomplete()
  }

  disconnect() {
    if (this._shInput) {
      this._shInput.removeEventListener("input", this._shOnInput)
      this._shInput.removeEventListener("blur", this._shOnBlur)
    }
    this.closeDropdown?.()
  }

  initSelfHostedAutocomplete() {
    if (!this.hasInputTarget) return
    this._shInput = this.inputTarget
    this._shInput.setAttribute("autocomplete", "off")
    let timer = null
    this._shOnInput = () => {
      if (this._suppressNextSearch) { this._suppressNextSearch = false; return } // skip the synthetic input from selectPlace
      clearTimeout(timer)
      if (hasZhuyin(this._shInput.value)) return // syllable still composing: search once it commits
      timer = setTimeout(() => this.searchPlaces(this._shInput.value.trim()), debounceMs())
    }
    this._shOnBlur = () => setTimeout(() => this.closeDropdown(), 150) // allow mousedown selection before close
    this._shInput.addEventListener("input", this._shOnInput)
    this._shInput.addEventListener("blur", this._shOnBlur)
  }

  async searchPlaces(query) {
    if (!query) { this.closeDropdown(); return }
    try {
      const res = await fetch(`/api/v1/places/search?q=${encodeURIComponent(query)}`, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      this.renderDropdown((await res.json())?.data || [])
    } catch (e) { log.error("Place search failed", e) }
  }

  renderDropdown(results) {
    this.closeDropdown()
    if (!results.length) return
    const ul = document.createElement("ul")
    ul.className = "absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-charcoal-200 bg-white shadow-lg text-sm"
    results.forEach((r) => {
      const li = document.createElement("li")
      li.className = "px-3 py-2 cursor-pointer hover:bg-crumb-100"
      li.textContent = r.address || r.name
      li.addEventListener("mousedown", (e) => { e.preventDefault(); this.selectPlace(r) })
      ul.appendChild(li)
    })
    const parent = this._shInput.parentElement
    if (parent && getComputedStyle(parent).position === "static") parent.style.position = "relative"
    parent?.appendChild(ul)
    this._shDropdown = ul
  }

  selectPlace(result) {
    this._suppressNextSearch = true
    this._shInput.value = result.address || result.name
    this._shInput.dispatchEvent(new Event("input", { bubbles: true })) // clear form validation error
    this.element.dispatchEvent(new CustomEvent("address-autocomplete:place-selected", {
      detail: {
        latitude: Number(result.latitude).toFixed(6),
        longitude: Number(result.longitude).toFixed(6),
        address: result.address || result.name,
        placeResult: result
      },
      bubbles: true
    }))
    this.closeDropdown()
  }

  closeDropdown() {
    if (this._shDropdown) { this._shDropdown.remove(); this._shDropdown = null }
  }
}
